import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../api';
import { sessionManager, isMobileBrowser } from '../utils/auth';

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const isMobile = isMobileBrowser();
        console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');
        
        // Get session_id from URL hash
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        console.log('🔐 Auth callback - session_id:', sessionId ? sessionId.substring(0, 10) + '...' : 'missing');

        if (!sessionId) {
          console.error('❌ No session_id found in URL');
          alert('Authentication failed: No session ID. Please try again.');
          navigate('/login', { replace: true });
          return;
        }

        // Exchange session_id for user session
        console.log('🔄 Exchanging session_id for session token...');
        const response = await auth.session(sessionId);
        console.log('✅ Session exchange response:', response.data);
        
        // Get session token from multiple sources
        const sessionToken = response.data.session_token || 
                           response.headers['x-session-token'] || 
                           sessionId;
        
        console.log('🎫 Session token:', sessionToken ? sessionToken.substring(0, 10) + '...' : 'not found');
        
        if (!sessionToken) {
          console.error('❌ No session token received');
          alert('Authentication failed: No session token. Please try again.');
          navigate('/login', { replace: true });
          return;
        }
        
        // Store session token
        sessionManager.setToken(sessionToken);
        
        // On mobile, wait longer for storage to complete
        const storageDelay = isMobile ? 500 : 200;
        console.log(`⏳ Waiting ${storageDelay}ms for storage...`);
        await new Promise(resolve => setTimeout(resolve, storageDelay));
        
        // Verify token was stored
        const storedToken = sessionManager.getToken();
        if (!storedToken) {
          console.error('❌ Token storage failed');
          alert('Authentication failed: Could not store session. Please check your browser settings and try again.');
          navigate('/login', { replace: true });
          return;
        }
        
        console.log('✅ Token stored successfully');
        
        // Test the session immediately
        console.log('🧪 Testing session with /auth/me...');
        try {
          const testResponse = await auth.me();
          console.log('✅ Session test successful:', testResponse.data);
        } catch (testError) {
          console.error('❌ Session test failed:', testError);
          alert('Authentication warning: Session may not persist. If you experience issues, please try logging in again.');
        }
        
        // Navigate to dashboard
        console.log('✅ Authentication complete, redirecting to dashboard');
        
        // Use replace to prevent back button issues
        window.location.replace('/dashboard');
        
      } catch (error) {
        console.error('❌ Auth callback error:', error);
        console.error('Error details:', error.response?.data);
        sessionManager.clearToken();
        
        // Show user-friendly error message
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        alert(`Authentication failed: ${errorMsg}\n\nPlease try logging in again.`);
        
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-red-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
